// Frame controller: practice (local rules) and online matches (server-authoritative rules).
//
// Match is the coordinator: it holds the match state and the phase, and every entry point the game loop, the cue,
// the socket (socket-state.ts) and the UI call. The work is done by the modules in ./match, by concern:
//   match/network.ts     socket sends, shot ids, resync, routing of game:state (the sync approach is described there)
//   match/shot.ts        our own shot: first hit, pots, frame streaming, the result
//   match/playback.ts    replay of the opponent's shot from the streamed frames
//   match/ballInHand.ts  placing the cue ball in the D and showing it to the opponent
//   match/endOfGame.ts   leaving, concede, rematch, left games (sessionStorage, reload), result dialog texts
//   match/hud.ts         HUD state, hints and toast texts
//   match/testHooks.ts   window.__snooker (?test=1, only in builds with the test hooks)
//   match/engine.ts      typed access to the camera and cue (Game.ts binds them; no Game/Cue imports here)
//   match/types.ts, match/messages.ts  the match state and the socket payloads
// The modules call back through this object (Match.applyFrame, Match.publish, ...), never a captured function, so a
// test script that wraps a Match method still sees every call.
import Balls from "./Balls";
import {GameStore} from "./features/game/game-store";
import {TEST_MODE} from "./testFlags";
import {FrameState, newFrame, Ruleset, ShotSummary} from "./shared/rules";
import {PlayerSettings} from "./settings";
import {engine} from "./match/engine";
import * as Shot from "./match/shot";
import * as Replay from "./match/playback";
import * as BallInHand from "./match/ballInHand";
import * as EndOfGame from "./match/endOfGame";
import * as Hud from "./match/hud";
import {installTestHooks} from "./match/testHooks";
import {Net, onlineInfoFrom, onState, shotStillPending} from "./match/network";
import type {MatchMode, OnlineInfo, Phase, Playback, ShotInProgress} from "./match/types";
import type {CueMoveMsg, FramesMsg, GameNoticeMsg, GameStateMsg, MatchFoundMsg, ShotStartMsg} from "./match/messages";

const Match = {
    mode: "practice" as MatchMode,
    frame: newFrame(1, 0, PlayerSettings.get().ruleset) as FrameState,   // the practice frame on the table at start-up
    online: null as OnlineInfo | null,
    phase: "aiming" as Phase,
    stepsPerFrame: 1,          // >1 only in test mode: fast-forward, the physics runs this many times real time
    // id of our shot that the server has not resolved yet (sent in the socket handshake on reconnect)
    activeShotId: null as string | null,
    // our shot while it rolls (shot.ts)
    shot: null as ShotInProgress | null,
    // the opponent's shot being replayed (playback.ts)
    playback: null as Playback | null,
    lastCueMoveSent: 0,
    lastHandMove: 0,
    practiceName: "You",
    // finished games this page closed (Back to menu or any other exit): anything still arriving for them is ignored.
    // Kept in sessionStorage so a game closed by reloading the tab stays closed on the reloaded page.
    leftGameIds: EndOfGame.loadLeftGameIds(),

    init() {
        BallInHand.listenForKeys();
        if (TEST_MODE) installTestHooks(Match);   // ?test=1 only, and only in builds with the hooks (testFlags.ts)
        // closing or reloading the tab while the result of a finished frame is shown leaves it, like "Back to menu"
        EndOfGame.listenForPagehide(Match);
        Match.publish();
    },

    // ----- queries used by the game loop / cue -----

    myTurn() {
        if (Match.mode === "practice") return true;
        return !!Match.online && Match.frame.turn === Match.online.you;
    },
    canShoot() {
        return Match.phase === "aiming" && Match.myTurn() && !Match.frame.over;
    },
    isShooting() {
        return Match.phase === "shooting";
    },
    physicsActive() {
        return Match.phase !== "spectating";
    },

    // ----- events from the game loop -----

    /** Cue.Shot → Game.State.Shot: the cue ball has just been struck. */
    onShotTaken() { Shot.onShotTaken(Match); },
    /** Cue.SendShot: tell the opponent a shot started (online). */
    onShotSent(_velocity: unknown) { Shot.onShotSent(Match); },
    /** Physics listener (Game.PhysicsListener): two balls touched; the first ball hit for the rules. */
    onContact(a: string, b: string) { Shot.onContact(Match, a, b); },
    onPotted(id: string) { Shot.onPotted(Match, id); },
    /** Called after each frame's physics advance while shooting. */
    afterStep() { Shot.afterStep(Match); },
    flushFrames() { Shot.flushFrames(Match); },
    /** Every ball has stopped after our shot. */
    onAllResting() { Shot.onAllResting(Match); },

    /** Per rendered frame: ball-in-hand placement and spectator playback. */
    update() {
        if (Match.canShoot() && Match.frame.ballInHand) {
            Match.ensureCueInHand();
            Match.moveCueBallInHand();
        }
        if (Match.phase === "spectating" && Match.playback) Match.playFrames();
    },

    /** Ball in hand: the cue ball must be on the table and inside the D (never left in a pocket). */
    ensureCueInHand() { BallInHand.ensureCueInHand(Match); },
    moveCueBallInHand() { BallInHand.moveCueBallInHand(Match); },

    /** Spectating: replay the opponent's shot from the streamed frames. */
    playFrames() { Replay.playFrames(Match); },
    finishPlayback() { Replay.finishPlayback(Match); },

    // ----- server messages -----

    onMatchFound(msg: MatchFoundMsg) { Hud.onMatchFound(msg); },

    onState(msg: GameStateMsg | null | undefined) { onState(Match, msg); },

    /** A message without state, e.g. "X left the table." after the frame ended. */
    onNotice(msg: GameNoticeMsg | null | undefined) { EndOfGame.onNotice(Match, msg); },

    applyServerState(msg: GameStateMsg) {
        const prevSeq = Match.online && Match.online.gameId === msg.gameId ? Match.online.seq : -1;
        const newGame = !Match.online || Match.online.gameId !== msg.gameId;
        Match.mode = "online";
        Match.online = onlineInfoFrom(msg);
        Match.shot = null;
        Match.playback = null;
        // our shot is resolved (applied, rejected or abandoned) unless the server says it is still pending
        if (!shotStillPending(msg, Match.activeShotId, prevSeq)) Match.activeShotId = null;
        Balls.applyLayout(msg.frame.positions);
        const summary: ShotSummary | null = msg.summary;
        Match.applyFrame(msg.frame, (msg.seq !== prevSeq && !newGame) ? summary : null, msg.status === "over");
        if (newGame) {
            Match.aimCameraForBreak();
        }
    },

    onShotStart(msg: ShotStartMsg) { Replay.onShotStart(Match, msg); },
    onFrames(msg: FramesMsg) { Replay.onFrames(Match, msg); },

    onCueMove(msg: CueMoveMsg) { BallInHand.onCueMove(Match, msg); },

    // ----- state application -----

    applyFrame(state: FrameState, summary: ShotSummary | null, over = state.over) {
        Match.frame = state;
        if (over) Match.phase = "over";
        else Match.phase = Match.myTurn() ? "aiming" : "watching";
        const cue = engine().cue;
        if (Match.phase === "aiming") {
            cue.Enabled = true;
            cue.Distance = 1.3;
            cue.Tracker.reset(performance.now());
        } else {
            cue.Enabled = false;
            if (cue.RenderedBody && cue.RenderedBody.position) cue.RenderedBody.position.y = 2000;
        }
        if (summary) GameStore.toast(...Hud.shotToast(summary, Match.mode, Match.online));
        const placing = Match.phase === "aiming" && state.ballInHand && !over;
        if (placing) Match.ensureCueInHand();
        const hint = Hud.hintAfterFrame(placing, GameStore.get().hint);
        if (hint !== undefined) GameStore.set({hint});
        if (over) EndOfGame.releasePointerForResult();
        Match.publish();
    },

    aimCameraForBreak() {
        // look up the table from behind the cue ball
        const {game} = engine();
        if (game.Camera) game.Camera.horizontalAngle = Math.PI;
    },

    publish() { Hud.publish(Match); },

    // ----- user actions -----

    /** The one way out of an online game, whatever the exit (Back to menu, header practice, a new queue, closing the tab). */
    leaveOnlineGame() { EndOfGame.leaveOnlineGame(Match); },
    /** Before queueing for a new match: a finished game still on screen is left first. */
    leaveFinishedGame() { EndOfGame.leaveFinishedGame(Match); },

    /** A new practice frame, with the ruleset chosen in the lobby unless one is given. */
    startPractice(ruleset: Ruleset = PlayerSettings.get().ruleset) {
        Match.leaveOnlineGame();
        Match.mode = "practice";
        Match.online = null;
        Match.shot = null;
        Match.activeShotId = null;
        Match.playback = null;
        const state = newFrame(1, 0, ruleset);
        Balls.applyLayout(state.positions);
        Match.applyFrame(state, null);
        Match.aimCameraForBreak();
    },

    concede() { EndOfGame.concede(Match); },
    rematch() { EndOfGame.rematch(Match); },
    backToMenu() { EndOfGame.backToMenu(Match); },

    /** The socket (re)connected: ask for the current game state. */
    resync() {
        Net.sync({gameId: Match.online?.gameId});
    },
};

export default Match;
